import 'react-datepicker/dist/react-datepicker.css';

import { Warning } from '@mui/icons-material';
import {
  Box,
  Button,
  Divider,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import * as _ from 'lodash';
import { useEffect, useState } from 'react';
import DatePicker from 'react-datepicker';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { useSelector } from 'react-redux';

import { AccessLevelType, type AccessSchedule } from '@/models/channel';
import { applicationDataSelector } from '@/store/slices/application-data';
import { dateUtils } from '@/utils/dateUtils';

import VNSelect from '../VNSelect/VNSelect';
import VNSortable from '../VNSortable/VNSortable';
import VNAccessScheduleCard from './VNAccessScheduleCard';

type Props = {
  name?: string;
};

const VNAccessSettings = ({ name = '' }: Props) => {
  const {
    control,
    setError,
    clearErrors,
    formState: { errors },
    getValues,
    setValue,
    watch,
  } = useFormContext();

  const { ouData } = useSelector(applicationDataSelector);
  const [showAddRow, setShowAddRow] = useState(false);
  const [effectiveAccessLevel, setEffectiveAccessLevel] = useState(getValues(`${name}.effectiveAccessLevel`));
  const [originalAccessLevel] = useState(getValues(`${name}.effectiveAccessLevel`));

  const { fields } = useFieldArray({
    control,
    name: `${name}.temporal.accessSchedules`,
  });

  // Watches
  const accessLevel = watch(`${name}.accessLevel`);

  useEffect(() => {
    recalculateEffectiveAccessStatus();
  }, [accessLevel]);

  console.log(watch(`${name}.effectiveAccessLevel`));

  const recalculateEffectiveAccessStatus = _.throttle(() => {
    // console.log('recalculateEffectiveAccessStatus', {});
    let newAccessLevel = _.cloneDeep(getValues(`${name}.accessLevel`));
    const accessSchedules: AccessSchedule[] = getValues(`${name}.temporal.accessSchedules`);
    if (accessSchedules != null && accessSchedules.length > 0) {
      const activeAccessSchedules: AccessSchedule[] = [];
      const now = dateUtils.now();
      for (let i = 0; i < accessSchedules.length; i += 1) {
        const sch = accessSchedules[i];
        sch.isActive = false;
        sch.isControlling = false;
        if (dateUtils.isBetween(now, sch.from, sch.to)) {
          sch.isActive = true;
          activeAccessSchedules.push(sch);
        }
      }
      // aalt 2025-06-20 #3289 the most restrictive access schedule always controls
      // ie. If there is a Private access schedule in the current window, it controls
      if (activeAccessSchedules != null && activeAccessSchedules.length > 0) {
        let controllingIndex = activeAccessSchedules.findIndex((x) => x.accessLevel === AccessLevelType.Private);
        if (controllingIndex > -1) {
          activeAccessSchedules[controllingIndex].isControlling = true;
          newAccessLevel = activeAccessSchedules[controllingIndex].accessLevel;
        } else {
          controllingIndex = activeAccessSchedules.findIndex((x) => x.accessLevel === AccessLevelType.Paywall);
          if (controllingIndex > -1) {
            activeAccessSchedules[controllingIndex].isControlling = true;
            newAccessLevel = activeAccessSchedules[controllingIndex].accessLevel;
          } else {
            controllingIndex = activeAccessSchedules.findIndex((x) => x.accessLevel === AccessLevelType.Free);
            if (controllingIndex > -1) {
              activeAccessSchedules[controllingIndex].isControlling = true;
              newAccessLevel = activeAccessSchedules[controllingIndex].accessLevel;
            }
          }
        }
      }
      setValue(`${name}.temporal.accessSchedules`, accessSchedules);
    }
    setEffectiveAccessLevel(newAccessLevel);
    setValue(`${name}.effectiveAccessLevel`, newAccessLevel);
  }, 1000);

  const [newSchedule, setNewSchedule] = useState({
    from: null,
    fromDate: null,
    to: null,
    toDate: null,
    accessLevel: '',
  });

  const handleNewScheduleChange = (field: string, value: string | Date) => {
    setNewSchedule((prev) => ({ ...prev, [field]: value }));
    clearErrors(field); // Clear error when user selects a value
  };

  const handleNewScheduleDateChange = (field: string, value: string | Date) => {
    // console.log('handleNewScheduleChange', { field, value });
    const newDate = dateUtils.toString(value as Date, ouData.timezone);
    if (field === 'from') {
      setNewSchedule((prev) => ({ ...prev, fromDate: value }));
      setNewSchedule((prev) => ({ ...prev, from: newDate }));
    }
    if (field === 'to') {
      setNewSchedule((prev) => ({ ...prev, toDate: value }));
      setNewSchedule((prev) => ({ ...prev, to: newDate }));
    }
    clearErrors(field); // Clear error when user selects a value
  };

  const addScheduleClick = () => {
    resetNewScheduleRow();
    setShowAddRow(true);
  };

  const resetNewScheduleRow = () => {
    setNewSchedule({ from: null, fromDate: null, to: null, toDate: null, accessLevel: '' });
  };

  const addSchedule = () => {
    if (!newSchedule.from && !newSchedule.to) {
      setError('from', { type: 'manual', message: 'From or To Date is required' });
      return;
    }
    if (!newSchedule.accessLevel) {
      setError('accessLevel', {
        type: 'manual',
        message: 'Access Level is required',
      });
      return;
    }
    // Ensure "To Date" is at least 24 hours greater than "From Date"
    if (newSchedule.from && newSchedule.to) {
      const fromDate = new Date(newSchedule.from);
      const toDate = new Date(newSchedule.to);
      const minAllowedToDate = new Date(fromDate.getTime() + 24 * 60 * 60 * 1000); // Add 24 hours
      if (toDate < minAllowedToDate) {
        // console.log('todate');
        setError('to', {
          type: 'manual',
          message: 'To Date must be at least 24 hours after From Date',
        });
        return;
      }
    }

    let accessSchedules = getValues(`${name}.temporal.accessSchedules`);
    if (accessSchedules == null) {
      accessSchedules = [];
    }
    accessSchedules.push(newSchedule);

    setValue(`${name}.temporal.accessSchedules`, accessSchedules);
    recalculateEffectiveAccessStatus();
    setShowAddRow(false);
  };

  return (
    <div className="vn-access-settings">
      <Box display="flex" gap={1} justifyContent="space-between" width="100%">
        {/* <span>{JSON.stringify(getValues(name))}</span> */}
        <VNSelect name={`${name}.accessLevel`} label="Default Access" options={ouData.metaData.defaultAccessLevelOptions} />
        {/* {statusFieldName && ouData && ouData.metaData.enablePaywall && <StatusDropdown name={statusFieldName} />} */}
      </Box>

      <Box mt={2} display="flex" gap={1} justifyContent="space-between" width="100%">
        <h2 style={{ fontWeight: 'bold', position: 'relative', left: '4px' }}>Access Schedules:</h2>
      </Box>

      {/* Render Added Schedules */}
      <Box mt={2}>
        {fields.length === 0 && (
          <Typography color="text.secondary" sx={{ marginLeft: '32px' }}>
            No schedules added.
          </Typography>
        )}
        {fields.length > 0 && (
          <div className="sortable-portlets access-schedule-sortable">
            <VNSortable
              name={`${name}.temporal.accessSchedules`}
              deletable
              itemTemplate={VNAccessScheduleCard}
              onDeleted={recalculateEffectiveAccessStatus}
              onSorted={recalculateEffectiveAccessStatus}
            />
          </div>
        )}
      </Box>

      {!showAddRow && (
        <Box mt={2} display="flex" gap={1} justifyContent="space-between" width="100%">
          <Button
            sx={{ whiteSpace: 'nowrap', height: '37px', width: '200px' }}
            variant="contained"
            color="success"
            onClick={addScheduleClick}
          >
            ADD SCHEDULE
          </Button>
        </Box>
      )}

      {showAddRow && (
        <Box mt={2} display="flex" gap={1} justifyContent="space-between" width="100%">
          {/* From Date */}
          <FormControl sx={{ flex: 1 }}>
            <DatePicker
              selected={newSchedule.fromDate}
              showTimeSelect
              timeIntervals={1}
              dateFormat="MM/dd/yyyy h:mm aa"
              onChange={(date) => handleNewScheduleDateChange('from', date)}
              // minDate={new Date()}
              customInput={
                <TextField fullWidth label={`From Date (${ouData.timezoneAbbreviation})`} size="small" autoComplete="false" />
              }
              popperPlacement="top-end" // Ensures correct positioning
              portalId="root" // Ensure it renders in the body
            />
            {errors.from && <FormHelperText>{errors.from.message as string}</FormHelperText>}
          </FormControl>

          {/* To Date */}
          <FormControl sx={{ flex: 1 }}>
            <DatePicker
              selected={newSchedule.toDate}
              showTimeSelect
              timeIntervals={1}
              dateFormat="MM/dd/yyyy h:mm aa"
              onChange={(date) => handleNewScheduleDateChange('to', date)}
              // minDate={new Date()}
              customInput={
                <TextField fullWidth label={`To Date (${ouData.timezoneAbbreviation})`} size="small" autoComplete="false" />
              }
              popperPlacement="top-end" // Ensures correct positioning
              portalId="root" // Ensure it renders in the body
            />
            {errors.to && <FormHelperText>{errors.to.message as string}</FormHelperText>}
          </FormControl>
          {/* Access Level */}
          <FormControl sx={{ flex: 1 }} size="small" error={!!errors.accessLevel}>
            <InputLabel sx={{ pl: 1, pr: 1 }}>Access</InputLabel>
            <Select value={newSchedule.accessLevel} onChange={(e) => handleNewScheduleChange('accessLevel', e.target.value)}>
              {ouData.metaData.defaultAccessLevelOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
            {errors.accessLevel && <FormHelperText>{errors.accessLevel.message as string}</FormHelperText>}
          </FormControl>

          {/* Add Schedule Button */}
          <Button
            sx={{ whiteSpace: 'nowrap', height: '37px', width: '100px' }}
            variant="contained"
            onClick={() => setShowAddRow(false)}
          >
            Cancel
          </Button>
          <Button
            sx={{ whiteSpace: 'nowrap', height: '37px', width: '100px' }}
            variant="contained"
            color="success"
            onClick={addSchedule}
          >
            Add
          </Button>
        </Box>
      )}

      <Divider style={{ marginTop: '24px' }} />
      <Box mt={3} mb={3} display="flex" gap={1} justifyContent="space-between" width="100%">
        <div>
          {effectiveAccessLevel !== originalAccessLevel && <Warning className="status-warning" />}
          <div style={{ display: 'inline-block', position: 'relative', paddingLeft: '12px', lineHeight: '18px' }}>
            Current Access Level: <span style={{ fontWeight: 'bold' }}>{originalAccessLevel || 'Public'}</span>
            <br />
            {effectiveAccessLevel !== originalAccessLevel && (
              <span>
                On Save, current access level will be: <span style={{ fontWeight: 'bold' }}>{effectiveAccessLevel}.</span>
              </span>
            )}
          </div>
        </div>
      </Box>
    </div>
  );
};

export default VNAccessSettings;
